import { ApiPath } from 'src/enums/apiPath';
import { apiRequest } from 'src/utils/api';

export type DashboardBillingPeriodType = '年' | '四半期' | '月';

export interface DashboardBillingTypeSalesItem {
  year: number;
  month: number;
  subscriptionSales: number;
  shotSales: number;
  axisLabel?: string;
  axisSubLabel?: string;
  tooltipLabel?: string;
}

export interface GetDashboardBillingTypeSalesParams {
  agency: string;
  periodType: DashboardBillingPeriodType;
  year?: number;
  month?: number;
}

interface GetDashboardBillingTypeSalesResponse {
  items?: DashboardBillingTypeSalesRawItem[];
  data?: DashboardBillingTypeSalesRawItem[];
}

interface DashboardBillingTypeSalesRawItem {
  year?: unknown;
  month?: unknown;
  subscriptionSales?: unknown;
  subscriptionRevenue?: unknown;
  subscriptionAmount?: unknown;
  subscriptionSale?: unknown;
  subscription?: unknown;
  subRevenue?: unknown;
  subAmount?: unknown;
  subSales?: unknown;
  shotSales?: unknown;
  shotRevenue?: unknown;
  shotAmount?: unknown;
  oneTimeSales?: unknown;
  oneTimeRevenue?: unknown;
  oneTimeAmount?: unknown;
  oneTime?: unknown;
  shot?: unknown;
  singleSales?: unknown;
  singleRevenue?: unknown;
  singleAmount?: unknown;
  axisLabel?: string;
  axisSubLabel?: string;
  tooltipLabel?: string;
  label?: string;
  subLabel?: string;
}

const PERIOD_TYPE_TO_API_VALUE: Record<
  DashboardBillingPeriodType,
  'year' | 'quarter' | 'month'
> = {
  年: 'year',
  四半期: 'quarter',
  月: 'month'
};

function toNumber(value: unknown, fallback = 0): number {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : fallback;
}

function pickBillingTypeSalesItems(payload: unknown) {
  if (Array.isArray(payload)) {
    return payload as DashboardBillingTypeSalesRawItem[];
  }

  if (!payload || typeof payload !== 'object') {
    return [];
  }

  const response = payload as GetDashboardBillingTypeSalesResponse;

  if (Array.isArray(response.items)) {
    return response.items;
  }

  if (Array.isArray(response.data)) {
    return response.data;
  }

  return [];
}

export async function getDashboardBillingTypeSales(
  params: GetDashboardBillingTypeSalesParams
) {
  const agencyId = normalizeDashboardAgencyId(params.agency);
  const requestParams: Record<string, string | number> = {
    periodType: PERIOD_TYPE_TO_API_VALUE[params.periodType]
  };

  if (agencyId) {
    requestParams.agencyId = agencyId;
  }

  if (typeof params.year === 'number') {
    requestParams.startYear = params.year;
  }

  if (typeof params.month === 'number') {
    requestParams.startMonth = params.month;
  }

  const res: any = await apiRequest.get({
    url: ApiPath.ADMIN_DASHBOARD_BILLING_TYPE_SALES,
    params: requestParams
  });

  const payloadSource =
    res?.data && Array.isArray(res.data) && !Array.isArray(res)
      ? res
      : res?.data;
  const payload = unwrapDashboardPayload<
    GetDashboardBillingTypeSalesResponse | DashboardBillingTypeSalesRawItem[]
  >(payloadSource);
  const normalizedItems = pickBillingTypeSalesItems(payload).map((item) => ({
    year: toNumber(item.year),
    month: toNumber(item.month),
    subscriptionSales: toNumber(
      item.subscriptionSales ??
        item.subscriptionRevenue ??
        item.subscriptionAmount ??
        item.subscriptionSale ??
        item.subscription ??
        item.subRevenue ??
        item.subAmount ??
        item.subSales
    ),
    shotSales: toNumber(
      item.shotSales ??
        item.shotRevenue ??
        item.shotAmount ??
        item.oneTimeSales ??
        item.oneTimeRevenue ??
        item.oneTimeAmount ??
        item.oneTime ??
        item.shot ??
        item.singleSales ??
        item.singleRevenue ??
        item.singleAmount
    ),
    axisLabel: item.axisLabel ?? item.label,
    axisSubLabel: item.axisSubLabel ?? item.subLabel,
    tooltipLabel: item.tooltipLabel
  }));

  return { items: normalizedItems };
}

export interface DashboardActiveSubscriptionItem {
  year: number;
  month: number;
  existingCount: number;
  newCount: number;
  cancelCount: number;
  cumulativeCount: number;
  axisLabel?: string;
  axisSubLabel?: string;
  tooltipLabel?: string;
}

export interface GetDashboardActiveSubscriptionParams {
  agency: string;
  periodType: DashboardBillingPeriodType;
  year?: number;
  month?: number;
}

interface GetDashboardActiveSubscriptionResponse {
  items?: DashboardActiveSubscriptionRawItem[];
  data?: DashboardActiveSubscriptionRawItem[];
  newRegistrationRate?: unknown;
  cancellationRate?: unknown;
  newRate?: unknown;
  cancelRate?: unknown;
}

interface DashboardActiveSubscriptionRawItem {
  year?: unknown;
  month?: unknown;
  existingCount?: unknown;
  existingMemberCount?: unknown;
  existingMembers?: unknown;
  totalExistingMembers?: unknown;
  activeCount?: unknown;
  activeMemberCount?: unknown;
  newCount?: unknown;
  newMemberCount?: unknown;
  newMembers?: unknown;
  newRegistrationCount?: unknown;
  cancelCount?: unknown;
  cancellationCount?: unknown;
  cancelMemberCount?: unknown;
  cancelledMemberCount?: unknown;
  cancelledMembers?: unknown;
  cumulativeCount?: unknown;
  cumulativeMemberCount?: unknown;
  totalCount?: unknown;
  totalMemberCount?: unknown;
  totalMembers?: unknown;
  value?: unknown;
  axisLabel?: string;
  axisSubLabel?: string;
  tooltipLabel?: string;
  label?: string;
  subLabel?: string;
}

interface DashboardApiEnvelope<T> {
  code?: string;
  message?: string;
  data?: T;
}

function unwrapDashboardPayload<T>(payload: unknown): T | undefined {
  if (!payload || typeof payload !== 'object') {
    return undefined;
  }

  const envelope = payload as DashboardApiEnvelope<T>;

  if (
    envelope.data &&
    typeof envelope.data === 'object' &&
    ('code' in envelope || 'message' in envelope)
  ) {
    return envelope.data;
  }

  return payload as T;
}

function pickActiveSubscriptionItems(payload: unknown) {
  if (Array.isArray(payload)) {
    return payload as DashboardActiveSubscriptionRawItem[];
  }

  if (!payload || typeof payload !== 'object') {
    return [];
  }

  const response = payload as GetDashboardActiveSubscriptionResponse;

  if (Array.isArray(response.items)) {
    return response.items;
  }

  if (Array.isArray(response.data)) {
    return response.data;
  }

  return [];
}

export async function getDashboardActiveSubscription(
  params: GetDashboardActiveSubscriptionParams
) {
  const agencyId = normalizeDashboardAgencyId(params.agency);
  const requestParams: Record<string, string | number> = {
    periodType: PERIOD_TYPE_TO_API_VALUE[params.periodType]
  };

  if (agencyId) {
    requestParams.agencyId = agencyId;
  }

  if (typeof params.year === 'number') {
    requestParams.startYear = params.year;
  }

  if (typeof params.month === 'number') {
    requestParams.startMonth = params.month;
  }

  const res: any = await apiRequest.get({
    url: ApiPath.ADMIN_DASHBOARD_ACTIVE_SUBSCRIPTION,
    params: requestParams
  });

  const payloadSource =
    res?.data && Array.isArray(res.data) && !Array.isArray(res)
      ? res
      : res?.data;
  const payload = unwrapDashboardPayload<
    | GetDashboardActiveSubscriptionResponse
    | DashboardActiveSubscriptionRawItem[]
  >(payloadSource);
  const normalizedItems = pickActiveSubscriptionItems(payload).map((item) => ({
    year: toNumber(item.year),
    month: toNumber(item.month),
    existingCount: toNumber(
      item.existingCount ??
        item.existingMemberCount ??
        item.existingMembers ??
        item.totalExistingMembers ??
        item.activeCount ??
        item.activeMemberCount
    ),
    newCount: toNumber(
      item.newCount ??
        item.newMemberCount ??
        item.newMembers ??
        item.newRegistrationCount
    ),
    cancelCount: toNumber(
      item.cancelCount ??
        item.cancellationCount ??
        item.cancelMemberCount ??
        item.cancelledMemberCount ??
        item.cancelledMembers
    ),
    cumulativeCount: toNumber(
      item.cumulativeCount ??
        item.cumulativeMemberCount ??
        item.totalCount ??
        item.totalMemberCount ??
        item.totalMembers ??
        item.value
    ),
    axisLabel: item.axisLabel ?? item.label,
    axisSubLabel: item.axisSubLabel ?? item.subLabel,
    tooltipLabel: item.tooltipLabel
  }));
  const response = payload as
    | GetDashboardActiveSubscriptionResponse
    | undefined;

  return {
    items: normalizedItems,
    newRegistrationRate: toNumber(
      response?.newRegistrationRate ?? response?.newRate
    ),
    cancellationRate: toNumber(
      response?.cancellationRate ?? response?.cancelRate
    )
  };
}

export interface DashboardOneTimeTransactionItem {
  year: number;
  month: number;
  count: number;
  axisLabel?: string;
  axisSubLabel?: string;
}

export interface GetDashboardOneTimeTransactionParams {
  agency: string;
  periodType: DashboardBillingPeriodType;
  year?: number;
  month?: number;
}

interface GetDashboardOneTimeTransactionResponse {
  items?: DashboardOneTimeTransactionRawItem[];
  data?: DashboardOneTimeTransactionRawItem[];
}

interface DashboardOneTimeTransactionRawItem {
  year?: unknown;
  month?: unknown;
  count?: unknown;
  totalCount?: unknown;
  value?: unknown;
  shotTransactionCount?: unknown;
  axisLabel?: string;
  axisSubLabel?: string;
  label?: string;
  subLabel?: string;
}

function normalizeDashboardAgencyId(agency: string) {
  if (!agency || agency === 'all' || agency === 'self') {
    return undefined;
  }

  if (agency === 'gc') {
    return 'operating';
  }

  return agency;
}

function pickOneTimeTransactionItems(payload: unknown) {
  if (Array.isArray(payload)) {
    return payload as DashboardOneTimeTransactionRawItem[];
  }

  if (!payload || typeof payload !== 'object') {
    return [];
  }

  const response = payload as GetDashboardOneTimeTransactionResponse;

  if (Array.isArray(response.items)) {
    return response.items;
  }

  if (Array.isArray(response.data)) {
    return response.data;
  }

  return [];
}

export async function getDashboardOneTimeTransaction(
  params: GetDashboardOneTimeTransactionParams
) {
  const agencyId = normalizeDashboardAgencyId(params.agency);
  const requestParams: Record<string, string | number> = {
    periodType: PERIOD_TYPE_TO_API_VALUE[params.periodType]
  };

  if (agencyId) {
    requestParams.agencyId = agencyId;
  }

  if (typeof params.year === 'number') {
    requestParams.startYear = params.year;
  }

  if (typeof params.month === 'number') {
    requestParams.startMonth = params.month;
  }

  const res: any = await apiRequest.get({
    url: ApiPath.ADMIN_DASHBOARD_ONE_TIME_TRANSACTION,
    params: requestParams
  });

  const payload = res?.data as
    | GetDashboardOneTimeTransactionResponse
    | DashboardOneTimeTransactionRawItem[]
    | undefined;
  const normalizedItems = pickOneTimeTransactionItems(payload).map((item) => ({
    year: toNumber(item.year),
    month: toNumber(item.month),
    count: toNumber(
      item.count ?? item.totalCount ?? item.value ?? item.shotTransactionCount
    ),
    axisLabel: item.axisLabel ?? item.label,
    axisSubLabel: item.axisSubLabel ?? item.subLabel
  }));

  return { items: normalizedItems };
}

export interface KpiSummaryMetric {
  current: number;
  lastMonth: number;
  growthRate: number;
  isInfinity: boolean;
}

export interface SubscriptionMembers {
  newSubscriptions: KpiSummaryMetric;
  activeSubscriptions: KpiSummaryMetric;
  adminCancels: KpiSummaryMetric;
  systemCancels: KpiSummaryMetric;
}

export interface KpiSummaryData {
  revenue: KpiSummaryMetric;
  transactions: KpiSummaryMetric;
  subscriptionSales: KpiSummaryMetric;
  oneTimeSales: KpiSummaryMetric;
  subscriptionMembers: SubscriptionMembers;
}

export interface GetDashboardOverviewResponse {
  kpiSummary?: KpiSummaryData;
  revenueStructure?: any;
}

export async function getDashboardOverview() {
  const res: any = await apiRequest.get({
    url: ApiPath.ADMIN_DASHBOARD_OVERVIEW
  });

  const data = res?.data?.data as any | undefined;

  if (!data) return null;
  return data;
}
